import { type ContactLinks } from '@/types';
import { Button } from '@/components/ui/button';
import { CalendarDays, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Props {
  links: ContactLinks;
  name?: string;
  /** When true, stacks vertically (default). False = horizontal compact row. */
  stacked?: boolean;
}

export function ContactButtons({ links, name, stacked = true }: Props) {
  const { t } = useTranslation('common');

  if (!links.whatsapp && !links.telegram && !links.book) return null;

  const bookLabel = name ? t('contact.book_with', { name }) : t('contact.book');

  return (
    <div className={stacked ? 'flex flex-col gap-2' : 'flex flex-wrap gap-2'}>
      {links.whatsapp && (
        <ContactLink
          href={links.whatsapp}
          icon={<WhatsAppIcon />}
          label={t('contact.whatsapp')}
          className="bg-[#25D366] text-white hover:bg-[#1ebe5d]"
          stacked={stacked}
        />
      )}
      {links.telegram && (
        <ContactLink
          href={links.telegram}
          icon={<Send className="h-4 w-4" />}
          label={t('contact.telegram')}
          className="bg-[#229ED9] text-white hover:bg-[#1a8fc4]"
          stacked={stacked}
        />
      )}
      {links.book && (
        <ContactLink
          href={links.book}
          icon={<CalendarDays className="h-4 w-4" />}
          label={bookLabel}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          stacked={stacked}
        />
      )}
    </div>
  );
}

function ContactLink({
  href,
  icon,
  label,
  className,
  stacked,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  className: string;
  stacked: boolean;
}) {
  return (
    <Button
      size="action"
      className={[
        stacked ? 'w-full justify-center' : 'min-w-0 flex-1 justify-center',
        className,
      ].join(' ')}
      asChild
    >
      <a href={href} target="_blank" rel="noopener noreferrer">
        {icon}
        <span className="truncate">{label}</span>
      </a>
    </Button>
  );
}

function WhatsAppIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="h-4 w-4 shrink-0"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.136.562 4.14 1.535 5.878L.057 23.5a.5.5 0 0 0 .614.612l5.757-1.505A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.686-.497-5.228-1.367l-.374-.216-3.878 1.015 1.03-3.762-.232-.389A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
    </svg>
  );
}
